import React, { useEffect, useRef, useState } from "react"
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import PlusIcon from "../../images/PlusIcon";
import { deleteClient, getClients } from "../../services/client.service";
import CloseIcon from "../../images/CloseIcon";
import { useResponsive } from "../../hook/useResponsive";
import ClientsIcon from "../../images/ClientsIcon";
import { showApiError } from "../../services/apiError";
import EditIconV2 from "../../images/EditIconV2";
import DeleteIconV2 from "../../images/DeleteIconV2";
import TagStatus from "../../components/TagStatus";
import { ConfirmSheet } from "../../components/mobile";
import {
    ListColumn,
    ListFilter,
    ListQuery,
    ListRowAction,
    ListScreen,
    ListScreenHandle,
    cells,
    t,
} from "../../components/list";

interface ClientRow {
    id: string;
    companyName: string;
    companyNumber: string;
    taxCode: string;
    companyType: number;
    emailAddress: string;
    status: { statusName: string } | null;
}

interface ClientsResponse {
    data: {
        count: number;
        data: ClientRow[];
    }
}

interface Company {
    name: string;
    value: number;
    id: number;
}
// Same i18n keys as the Type filter below.
const companies: Company[] = [
    { name: 'Sales client', value: 1, id: 1 },
    { name: 'Supplier', value: 2, id: 2 },
    { name: 'Sales client & supplier', value: 3, id: 3 }
];
const Clients = () => {
    const preLanguage = localStorage.getItem("language");
    const { isMobile, isTouchLayout } = useResponsive();
    const companyId: string = localStorage.getItem("companyId") || "";
    const [language, setLanguage] = useState<any>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<ListScreenHandle>(null);
    const inforTable: any = {
        img: <ClientsIcon />,
        title: language && language?.["No clients yet"] ? language?.["No clients yet"] : "No clients yet", path: "/clients/new",
        desc: language && language?.["Add a new client to get started."] ? language?.["Add a new client to get started."] : "Add a new client to get started."
    }

    const [showDelete, setShowDelete] = useState<boolean>(false);
    const [idDelete, setIdDelete] = useState<string>("");
    const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
            event.preventDefault();
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    };
    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };

    }, []);
    useEffect(() => {
        if (window.languageContext) {
            setLanguage(window.languageContext.language);
        }
    }, [window, window.languageContext]);

    /** One page of clients: the search box maps onto `searchValue` (name, company no, tax no). */
    const fetchClients = async (query: ListQuery) => {
        webLoadingFalse();
        const response: ClientsResponse = await getClients(
            query.page,
            query.limit,
            query.search.trim(),
            companyId,
            query.filters.clientType ?? ""
        );
        return { rows: response?.data?.data ?? [], count: response?.data?.count ?? 0 };
    };

    const goToClient = (id: string) => webNavigate('/' + preLanguage + '/clients/' + id);
    const typeLabel = (companyType: number, lang: any) => {
        const name = companies.find((v) => v.value === Number(companyType))?.name ?? "";
        return name ? t(lang, name, name) : "";
    };

    const handleShowDelete = async (id: string) => {
        setShowDelete(true);
        setIdDelete(id)
    }
    const handleDeleteClient = async () => {
        try {
            const response = await deleteClient(idDelete);
        } catch (error) {
            console.error("Error loading terms:", error);
            showApiError(error, { language });
        } finally {
            listRef.current?.reload();
            setShowDelete(false)
        }
    }

    /* ---------------- List kit configuration ---------------- */

    const columns: ListColumn<ClientRow>[] = [
        {
            key: 'no',
            label: 'No',
            width: 64,
            value: (_c, ctx) => (ctx.page - 1) * ctx.limit + ctx.index + 1,
            mobile: 'hidden',
        },
        {
            key: 'companyName',
            label: 'Name',
            width: 240,
            hideable: false,
            render: cells.primary(c => c.companyName),
            mobile: 'title',
            onClick: c => goToClient(String(c.id)),
        },
        { key: 'companyNumber', label: 'Company No', width: 140, value: c => c.companyNumber ?? '', mobile: 'subtitle' },
        { key: 'taxCode', label: 'Tax No', width: 140, value: c => c.taxCode ?? '', mobile: 'sheet' },
        {
            key: 'companyType',
            label: 'Type',
            width: 120,
            value: (c, ctx) => typeLabel(c.companyType, ctx.language),
            mobile: 'subtitle',
        },
        {
            key: 'emailAddress',
            label: 'Email',
            width: 220,
            value: c => c.emailAddress ?? '',
            // Long addresses wrap in the record sheet instead of widening it.
            mobileRender: c => <span style={{ overflowWrap: 'anywhere' }}>{c.emailAddress ?? ''}</span>,
            mobile: 'sheet',
        },
        {
            key: 'status',
            label: 'Status',
            width: 120,
            value: c => c.status?.statusName ?? '',
            render: (c, ctx) => {
                const name = c.status?.statusName ?? '';
                return <TagStatus value={name} label={name ? t(ctx.language, name, name) : undefined} />;
            },
            mobile: 'trailing',
        },
    ];

    const filters: ListFilter[] = [
        {
            key: 'clientType',
            label: 'Type',
            kind: 'select',
            options: [
                { value: '1', label: t(language, 'Sales client', 'Sales client') },
                { value: '2', label: t(language, 'Supplier', 'Supplier') },
            ],
        },
    ];

    const rowActions: ListRowAction<ClientRow>[] = [
        {
            key: 'edit',
            label: 'Edit',
            icon: <EditIconV2 />,
            sheetIcon: 'inventory',
            onRun: c => goToClient(String(c.id)),
        },
        {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteIconV2 />,
            sheetIcon: 'close',
            danger: true,
            onRun: c => handleShowDelete(String(c.id)),
        },
    ];

    return (
        <>
            {/* Desktop is a flex column so the list fills the height left under the title row. */}
            <div
                className="h-full relative"
                style={{
                    height: '100%',
                    overflowY: isTouchLayout ? "auto" : undefined,
                    paddingBottom: isTouchLayout ? "24px" : undefined,
                    display: isTouchLayout ? undefined : 'flex',
                    flexDirection: isTouchLayout ? undefined : 'column',
                }}
            >
                <div className="lg:flex items-center justify-between" >
                    <p
                        style={{ color: '#222525' }}
                        className="text-[24px] leading-[130%] font-medium font-inter"
                    >
                        {language && language?.['Clients'] ? language?.['Clients'] : 'Clients'}
                    </p>
                    <div
                        className="cursor-pointer flex items-center justify-center"
                        style={{
                            gap: '8px',
                            padding: "8px 24px 8px 16px",
                            backgroundColor: '#0F7679',
                            borderRadius: '30px',
                            width: 'fit-content',
                            marginTop: isMobile ? "20px" : '0px',
                        }}
                        onClick={() => {
                            webNavigate('/' + preLanguage + '/clients/new');
                        }}
                    >
                        <div style={{ width: '24px', height: '24px' }} className="flex items-center justify-center">
                            <PlusIcon />
                        </div>
                        <p
                            style={{ color: '#FFFFFF' }}
                            className="text-[14px] leading-[130%] font-medium font-inter"
                        >
                            {language && language?.['Add client'] ? language?.['Add client'] : 'Add client'}
                        </p>
                    </div>
                </div>
                <ListScreen
                    ref={listRef}
                    language={language}
                    columns={columns}
                    fetchRows={fetchClients}
                    searchPlaceholder="Search clients"
                    searchInputRef={searchInputRef}
                    filters={filters}
                    rowActions={rowActions}
                    countNoun="clients"
                    emptyState={inforTable}
                />
            </div>
            {isTouchLayout ? (
                <ConfirmSheet
                    open={showDelete}
                    title={language && language?.["Delete clients?"] ? language?.["Delete clients?"] : "Delete clients?"}
                    message={language && language?.["You will permanently delete the client"] ? language?.["You will permanently delete the client"] : "You will permanently delete the client"}
                    confirmLabel={language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
                    cancelLabel={language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
                    onConfirm={handleDeleteClient}
                    onCancel={() => setShowDelete(false)}
                />
            ) : (
                <div
                    className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showDelete ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                        } transition-opacity duration-300 ease-in-out`}
                    style={{ backgroundColor: "#22252552", zIndex: "200", transition: "opacity 0.3s ease, visibility 0.3s ease", width: "100%" }}
                >
                    <div
                        className={`transform transition-transform duration-300 ease-in-out ${showDelete ? "translate-y-0" : "translate-y-4 opacity-0"}`} style={{
                            padding: "24px", borderRadius: "24px", backgroundColor: "#FFFFFF", width: "530px", transition: "transform 0.3s ease, opacity 0.3s ease",
                        }}
                    >
                        <div
                            className="flex items-center justify-between"
                        >
                            <p style={{ fontSize: '20px', color: '#222525' }} className="font-semibold font-inter leading-[24px]">
                                {language && language?.["Delete clients?"] ? language?.["Delete clients?"] : "Delete clients?"}
                            </p>
                            <div
                                onClick={() => setShowDelete(false)}
                                className="flex items-center justify-center cursor-pointer"
                                style={{
                                    width: "24px",
                                    height: "24px",
                                }}
                            >
                                <CloseIcon />
                            </div>
                        </div>
                        <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
                            <p className="text-[14px] font-inter leading-[24px]" style={{ color: '#6A7878' }}>
                                {language && language?.["You will permanently delete the client"] ? language?.["You will permanently delete the client"] : "You will permanently delete the client"}
                            </p>
                            {/* <span className="text-main">[sale ID]</span> sale. */}
                        </div>
                        <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
                            <div
                                onClick={() => setShowDelete(false)}
                                className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                                style={{
                                    borderRadius: '30px',
                                    padding: '10px 24px',
                                    border: '1px solid #C9D7D8',
                                    color: '#222525',
                                }}
                            >
                                {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
                            </div>
                            <div
                                onClick={handleDeleteClient}
                                className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                                style={{
                                    borderRadius: '30px',
                                    padding: '10px 24px',
                                    backgroundColor: '#BA0E13',
                                    color: '#FFFFFF',
                                }}
                            >
                                {language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
};

export default Clients;